import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pagination } from "antd";
import { Pencil, Plus, RepeatIcon, Trash2 } from "lucide-react";
import { notify } from "@/components/common/Notification";
import { PtsExpenseSummaryService } from "@/services/APIs";
import type { ExpenseItem } from "@/services/APIs/PtsExpenseSummaryService";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { cn } from "../../lib/utils";
import AddEditExpenseModal from "./AddEditExpenseModal";
import RecurringExpenseModal from "./RecurringExpenseModal";
import { ExpenseCategoryBadge, ExpenseFrequencyBadge } from "./ExpenseBadges";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import ExpenseFilters from "./ExpenseFilters";
import {
  EMPTY_EXPENSE_FILTERS,
  type ExpenseFilterValues,
  USD_TO_INR_RATE,
  EXPENSE_STATUS_OPTIONS,
} from "./constants";
import {
  avatarClass,
  formatDateDisplay,
  formatInr,
  formatMoney,
  formatQuantity,
  initialOf,
  isInrCurrency,
  monthName,
  monthShortLabel,
  statusBadgeClass,
  toInr,
  uniqueSorted,
} from "./utils";

const TABLE_COLUMN_COUNT = 11;
const SUMMARY_PAGE_SIZE = 1000;
const SEARCH_DEBOUNCE_MS = 400;

const ExpenseSummaryPage = () => {
  const queryClient = useQueryClient();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [monthIndex, setMonthIndex] = useState(() => new Date().getMonth());
  const [filterValues, setFilterValues] = useState<ExpenseFilterValues>(
    EMPTY_EXPENSE_FILTERS
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseItem | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<ExpenseItem | null>(null);
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(filterValues.search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [filterValues.search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterValues.category,
    filterValues.frequency,
    filterValues.status,
    debouncedSearch,
    year,
    monthIndex,
  ]);

  const listFilters = useMemo(
    () => ({
      month: monthName(monthIndex),
      year,
      category: filterValues.category,
      frequency: filterValues.frequency,
      status: filterValues.status,
      search: debouncedSearch || undefined,
    }),
    [
      monthIndex,
      year,
      filterValues.category,
      filterValues.frequency,
      filterValues.status,
      debouncedSearch,
    ]
  );

  const { data, isLoading } = useQuery({
    queryKey: ["expense-summary", "list", listFilters, currentPage, pageSize],
    queryFn: () =>
      PtsExpenseSummaryService.getExpenses({
        ...listFilters,
        page: currentPage,
        page_size: pageSize,
      }),
  });

  // Same filters without pagination so summary cards reflect the full filtered set.
  const { data: summaryData } = useQuery({
    queryKey: ["expense-summary", "summary", listFilters],
    queryFn: () =>
      PtsExpenseSummaryService.getExpenses({
        ...listFilters,
        page: 1,
        page_size: SUMMARY_PAGE_SIZE,
      }),
  });

  const expenses = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const summaryExpenses = summaryData?.results ?? expenses;

  const { data: exchangeRateData } = useQuery({
    queryKey: ["usd-to-inr-rate"],
    queryFn: async () => {
      try {
        const res = await fetch(
          "https://api.frankfurter.dev/v1/latest?base=USD&symbols=INR"
        );
        if (!res.ok) {
          throw new Error("Failed to fetch exchange rate");
        }
        const json = await res.json();
        const rate = json.rates.INR as number;
        console.log("Fetched USD to INR rate successfully:", rate);
        return rate;
      } catch (error) {
        console.error(
          "Error fetching exchange rate from API, using fallback:",
          error
        );
        throw error;
      }
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const exchangeRate = exchangeRateData ?? USD_TO_INR_RATE;

  useEffect(() => {
    console.log("Current USD to INR exchange rate used:", exchangeRate);
  }, [exchangeRate]);

  const vendorOptions = useMemo(
    () => uniqueSorted(summaryExpenses.map(item => item.vendor)),
    [summaryExpenses]
  );

  // const summary = useMemo(
  //   () => computeExpenseSummary(summaryExpenses, 2, exchangeRate),
  //   [summaryExpenses, exchangeRate]
  // );

  const periodLabel = monthShortLabel(year, monthIndex);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => PtsExpenseSummaryService.deleteExpense(id),
    onSuccess: () => {
      notify.success("Expense deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["expense-summary"] });
      setDeleteTarget(null);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      PtsExpenseSummaryService.updateExpense(id, { status }),
    onSuccess: () => {
      notify.success("Status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["expense-summary"] });
    },
    onError: (error: any) => {
      notify.error(error?.message ?? "Failed to update status");
    },
  });

  const handleMonthChange = (nextYear: number, nextMonthIndex: number) => {
    setYear(nextYear);
    setMonthIndex(nextMonthIndex);
  };

  const handleAddExpense = () => {
    setEditingExpense(null);
    setIsModalOpen(true);
  };

  const handleEditExpense = (expense: ExpenseItem) => {
    setEditingExpense(expense);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingExpense(null);
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Expense Summary
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Track and manage all company expenses in one place
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsRecurringModalOpen(true)}
          >
            <RepeatIcon className="mr-1.5 h-4 w-4 text-indigo-600" />
            Create Recurring
          </Button>
          <Button type="button" onClick={handleAddExpense} className="h-9">
            <Plus className="mr-1.5 h-4 w-4" />
            Add Expense
          </Button>
        </div>
      </div>

      <ExpenseFilters
        year={year}
        monthIndex={monthIndex}
        onMonthChange={handleMonthChange}
        values={filterValues}
        onChange={setFilterValues}
      />

      {/* <ExpenseSummaryCards summary={summary} periodLabel={periodLabel} /> */}

      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="p-0">
          <div className="border-b border-gray-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-[#111827]">
              Expense List
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              All expenses for {periodLabel}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                  <th className="px-5 py-3 font-medium">Expense</th>
                  <th className="px-5 py-3 font-medium">Category</th>
                  <th className="px-5 py-3 font-medium">Vendor</th>
                  <th className="px-5 py-3 font-medium">Frequency</th>
                  <th className="px-5 py-3 font-medium">Quantity</th>
                  <th className="px-5 py-3 font-medium">Unit Cost</th>
                  <th className="px-5 py-3 font-medium">Total Expense</th>
                  <th className="px-5 py-3 font-medium">Total Expense(₹)</th>
                  <th className="px-5 py-3 font-medium">Billing Date</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Is Recurring</th>
                  <th className="w-24 px-5 py-3 font-medium">Actions </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={TABLE_COLUMN_COUNT}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : expenses.length === 0 ? (
                  <tr>
                    <td
                      colSpan={TABLE_COLUMN_COUNT}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No expenses found for {periodLabel}.
                    </td>
                  </tr>
                ) : (
                  expenses.map(item => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-50 last:border-0"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <span
                            className={cn(
                              "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold",
                              avatarClass(item.title)
                            )}
                          >
                            {initialOf(item.title)}
                          </span>
                          <span className="font-medium text-[#111827]">
                            {item.title || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <ExpenseCategoryBadge category={item.category} />
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.vendor || "—"}
                      </td>
                      <td className="px-5 py-3.5">
                        <ExpenseFrequencyBadge frequency={item.frequency} />
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatQuantity(item.quantity)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatMoney(item.unit_cost, item.currency)}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="font-semibold text-[#111827]">
                          {formatMoney(item.total_expense, item.currency)}
                        </div>
                        {!isInrCurrency(item.currency) && (
                          <div className="text-xs text-gray-400">
                            {formatInr(
                              toInr(
                                item.total_expense,
                                item.currency,
                                exchangeRate
                              )
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="font-semibold text-[#111827]">
                          {formatInr(
                            toInr(
                              item.total_expense,
                              item.currency,
                              exchangeRate
                            )
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDisplay(item.billing_date)}
                      </td>
                      <td className="px-5 py-3.5">
                        <Select
                          value={item.status || "Active"}
                          onValueChange={status =>
                            updateStatusMutation.mutate({ id: item.id, status })
                          }
                          disabled={updateStatusMutation.isPending}
                        >
                          <SelectTrigger
                            className={cn(
                              "inline-flex h-7 w-28 cursor-pointer items-center justify-between rounded-full border border-gray-200 px-2.5 py-0.5 text-xs font-medium shadow-none transition-all hover:bg-opacity-80 focus:ring-0 focus:ring-offset-0 [&>span]:line-clamp-1",
                              statusBadgeClass(item.status)
                            )}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-50 bg-white">
                            {EXPENSE_STATUS_OPTIONS.map(status => (
                              <SelectItem
                                key={status}
                                value={status}
                                className="cursor-pointer text-xs"
                              >
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      <td className="text-center">
                        {item.is_recurring ? (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[13px] font-medium text-green-600">
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[13px] font-medium text-red-600">
                            No
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-md text-gray-500 hover:bg-gray-100 hover:text-[#111827]"
                            aria-label={`Edit ${item.title}`}
                            onClick={() => handleEditExpense(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                            aria-label={`Delete ${item.title}`}
                            onClick={() => setDeleteTarget(item)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalCount > 0 && (
            <div className="flex items-center justify-end border-t p-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(page, size) => {
                  if (size && size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                    return;
                  }
                  setCurrentPage(page);
                }}
                onShowSizeChange={(_current, size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                pageSizeOptions={["10", "25", "50", "100"]}
                disabled={isLoading}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <AddEditExpenseModal
        open={isModalOpen}
        onClose={handleCloseModal}
        expense={editingExpense}
        vendorOptions={vendorOptions}
      />

      <RecurringExpenseModal
        open={isRecurringModalOpen}
        onClose={() => setIsRecurringModalOpen(false)}
        defaultYear={year}
      />

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={isOpen => !isOpen && setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete expense?</AlertDialogTitle>
            <AlertDialogDescription>
              {`"${deleteTarget?.title ?? ""}" will be permanently removed. This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              disabled={deleteMutation.isPending}
              onClick={event => {
                event.preventDefault();
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id);
                }
              }}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpenseSummaryPage;
